import { InsightsNavbar } from "@/components/InsightsNavbar";
import { AnimatedSection } from "@/components/AnimatedSection";
import { ScrollProgressIndicator } from "@/components/ScrollProgressIndicator";
import { Footer } from "@/components/Footer";
import { InsightsLeadModal } from "@/components/InsightsLeadModal";
import { InsightsBreadcrumb } from "@/components/InsightsBreadcrumb";
import { InsightSEO } from "@/hooks/useInsightSEO";
import { RelatedTools } from "@/components/RelatedTools";
import { InsightEngagementTracker } from "@/components/InsightEngagementTracker";
import { InsightFAQSection } from "@/components/InsightFAQSection";
import { InsightBenchmarks } from "@/components/InsightBenchmarks";
import { TableOfContents } from "@/components/TableOfContents";
import { Badge } from "@/components/Badge";
import { StatPill } from "@/components/StatPill";
import { Link } from "react-router-dom";
import {
  Receipt,
  Lightbulb,
  ArrowRight,
  LineChart,
  DollarSign,
  Users,
  TrendingUp,
  Gauge,
  Target,
  Eye,
  Layers,
  Code,
  Blocks,
  Building,
  MessageSquare,
  Cpu,
  Wallet,
} from "lucide-react";
import amanImage from "@/assets/aman-sajid.png";

const HeroSection = () => (
  <section className="pt-8 sm:pt-12">
    <Badge>
      <Receipt className="h-3 w-3 mr-1" />
      SaaS Pricing and AI Economics
    </Badge>

    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mt-4 mb-3 leading-tight">
      Per Seat Is Dying: How Token Pricing Is Rewriting SaaS Budgets
    </h1>

    <p className="text-lg sm:text-xl text-muted-foreground mb-6 max-w-3xl">
      Per seat pricing was the deal that made SaaS work. The vendor got
      recurring revenue, the buyer got a predictable bill, and finance could
      forecast software cost by headcount. It held for two decades. AI broke
      it. A model call costs the vendor real money every time it runs, and
      that cost scales with usage rather than with users. The result for
      buyers is a software bill that behaves like a cloud bill: variable,
      hard to forecast, and capable of blowing through a budget in months.
    </p>

    <div className="flex flex-wrap gap-3 mb-8">
      <StatPill value="20 years" label="per seat was the SaaS default" />
      <StatPill value="3 models" label="tokens, credits, outcomes" />
      <StatPill value="Month end" label="when metered overruns get discovered" />
      <StatPill value="Unbounded" label="an AI line item with no cap" />
    </div>

    <div className="bg-muted/30 border border-border rounded-xl p-6 sm:p-8">
      <h2
        id="overview"
        className="text-xl sm:text-2xl font-semibold text-foreground mb-4 flex items-center gap-2"
      >
        <Gauge className="h-5 w-5 text-primary" />
        The shift in three lines
      </h2>
      <div className="grid md:grid-cols-3 gap-6">
        <div>
          <p className="text-muted-foreground">
            <strong className="text-foreground">1. Per seat worked because software had near zero marginal cost.</strong>{" "}
            Charging per user was pure margin at scale, and buyers accepted it
            because it was legible: ten people, ten licences, one number on
            the invoice.
          </p>
        </div>
        <div>
          <p className="text-muted-foreground">
            <strong className="text-foreground">2. AI has real per call cost.</strong>{" "}
            Every AI feature is a metered call to a model. A heavy user can
            cost the vendor many times what a light user costs, and per seat
            pricing cannot tell them apart.
          </p>
        </div>
        <div>
          <p className="text-muted-foreground">
            <strong className="text-foreground">3. So vendors moved the meter to you.</strong>{" "}
            Tokens, credits and outcome pricing turn software into a variable
            cost that scales with success and surprises at month end.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const Takeaway = () => (
  <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 sm:p-8">
    <div className="flex items-start gap-4">
      <div className="p-2 bg-primary/10 rounded-lg shrink-0">
        <Lightbulb className="h-6 w-6 text-primary" />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Key Takeaway</h3>
        <p className="text-muted-foreground mb-4">
          Per seat is not vanishing overnight, but for anything with AI at its
          core, expect a base fee plus a meter, and increasingly outcome
          pricing. Buyers should demand separate AI lines, hard caps with
          alerts, usage transparency, and cost per outcome models. Token
          pricing is the vendor passing its cost problem to you. Outcome
          pricing is the vendor owning it.
        </p>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-primary" />
            <span className="text-foreground font-medium">Forecast by behaviour, not headcount</span>
          </div>
          <div className="flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-primary" />
            <span className="text-foreground font-medium">Success is now the cost driver</span>
          </div>
          <div className="flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-primary" />
            <span className="text-foreground font-medium">Buy predictability back in the contract</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const FounderQuote = () => (
  <div className="bg-card border border-border rounded-xl p-6 sm:p-8">
    <div className="flex flex-col sm:flex-row gap-6 items-start">
      <img
        src={amanImage}
        alt="Aman Sajid"
        className="w-16 h-16 rounded-full object-cover border-2 border-primary/20 shrink-0"
      />
      <div>
        <blockquote className="text-lg sm:text-xl text-foreground italic mb-4">
          "For twenty years I could tell a client exactly what their software
          would cost next year: count the people, multiply. That is gone for
          anything with AI inside it. We now watch tools that cost the same
          for a year suddenly cost three times more in a good month, and
          nobody hired anyone. The meter just ran. If your finance team is
          still modelling software as a fixed cost, they are modelling 2019."
        </blockquote>

        <div className="bg-muted/40 border border-border rounded-lg p-4 mb-4">
          <p className="text-sm font-semibold text-foreground mb-3">
            Aman's read on the shift:
          </p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">Adoption used to be free after the licence.</strong>{" "}
                On a meter, adoption is the invoice. Microsoft's internal
                Claude Code story is what that looks like at scale: rollout
                succeeded, usage beat forecast, the plug got pulled at fiscal
                year end.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">Vendors are less aligned with you than they were.</strong>{" "}
                Under per seat, an efficient product protected their margin.
                Under tokens, a verbose product earns more. Inspect usage
                accordingly.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">Not everything needs a meter.</strong>{" "}
                Project management, documentation, internal tools and workflow
                software have near zero marginal cost. That is why we built
                Lottae on a flat $3.50 per user with no tiers: for that
                category, a flat number is still the honest model.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">What we tell IT Dukes clients:</strong>{" "}
                separate the AI line, cap it, alert at 50 and 80 percent,
                review it monthly with a named owner, and prefer outcome
                pricing whenever a vendor offers it.
              </span>
            </li>
          </ul>
        </div>

        <div>
          <p className="font-semibold text-foreground">Aman Sajid</p>
          <p className="text-sm text-muted-foreground">Founder, IT Dukes</p>
        </div>
      </div>
    </div>
  </div>
);

const marketShift = [
  {
    icon: Code,
    value: "Usage limits",
    label: "Coding tools",
    sub: "Flat monthly plans moved to usage based limits during 2025 after power users on unlimited plans generated costs far above the subscription. Cursor's mid 2025 pricing change and the reaction to it was the most public example.",
    color: "text-primary",
  },
  {
    icon: Blocks,
    value: "Credits",
    label: "App builders",
    sub: "Lovable, Replit and Bolt price in credits or effort based units rather than seats. Every message costs something.",
    color: "text-blue-500",
  },
  {
    icon: Building,
    value: "Flex Credits",
    label: "Enterprise platforms",
    sub: "Salesforce introduced consumption based Flex Credits for Agentforce alongside per conversation pricing. Microsoft Copilot launched per seat but pairs with metered agent capacity.",
    color: "text-amber-500",
  },
  {
    icon: MessageSquare,
    value: "Per resolution",
    label: "Support AI",
    sub: "Zendesk and Intercom moved AI agents to per resolution pricing: you pay when a ticket is resolved, not per seat.",
    color: "text-purple-500",
  },
  {
    icon: Cpu,
    value: "Always metered",
    label: "Model providers",
    sub: "OpenAI, Anthropic and Google have always billed per token. Every SaaS AI feature is a resale of that meter, with margin.",
    color: "text-orange-500",
  },
  {
    icon: Wallet,
    value: "Month end",
    label: "When you find out",
    sub: "Metered charges surface at month end. Overruns are discovered, not planned. Finance teams that learned this with AWS are learning it again with software.",
    color: "text-destructive",
  },
];

const MarketShift = () => (
  <section className="py-10 sm:py-12">
    <h2
      id="market-shift"
      className="text-2xl sm:text-3xl font-bold text-foreground mb-4 flex items-center gap-2"
    >
      <LineChart className="h-6 w-6 text-primary" />
      Where the market has already moved
    </h2>
    <p className="text-muted-foreground mb-8 max-w-3xl">
      The pattern is visible across every category that added AI. None of
      these vendors chose usage pricing as a trend. It is arithmetic they
      cannot escape once they are paying by the token themselves.
    </p>

    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
      {marketShift.map((n) => (
        <div
          key={n.label}
          className="bg-card border border-border rounded-xl p-5 hover:border-primary/30 transition-colors"
        >
          <div className={`p-2 rounded-lg bg-muted w-fit mb-3 ${n.color}`}>
            <n.icon className="h-5 w-5" />
          </div>
          <div className={`text-2xl font-bold mb-1 ${n.color}`}>{n.value}</div>
          <div className="font-semibold text-foreground mb-1">{n.label}</div>
          <p className="text-sm text-muted-foreground">{n.sub}</p>
        </div>
      ))}
    </div>
  </section>
);

const drivers = [
  {
    icon: Users,
    title: "Forecasting by headcount stops working",
    body:
      "Cost now depends on behaviour, not on the number of people. Two teams of the same size can generate very different bills, and the same team can double its usage in a quarter without hiring anyone.",
  },
  {
    icon: TrendingUp,
    title: "Success gets expensive",
    body:
      "Under per seat, adoption was free after the licence. Under tokens, adoption is the cost driver. The better the tool works, the more it gets used, the higher the bill.",
  },
  {
    icon: Gauge,
    title: "Separate the AI line and negotiate caps, not just rates",
    body:
      "Insist on seeing metered charges as their own line even inside a bundle. A good per token rate is worth less than a hard monthly cap with alerts at 50 and 80 percent. Predictability is the thing you lost. Buy it back in the contract.",
  },
  {
    icon: Target,
    title: "Model cost per outcome, not cost per user",
    body:
      "Cost per ticket resolved, per meeting booked, per feature shipped. Usage pricing only makes sense if you can connect spend to results. If you cannot, the meter is running on faith.",
  },
  {
    icon: Eye,
    title: "Prefer vendors who show you the meter",
    body:
      "Dashboards, per user usage, per feature usage. Vendors who hide consumption are pricing on your inattention.",
  },
  {
    icon: Layers,
    title: "Keep flat pricing where it still belongs",
    body:
      "Project management, documentation, internal tools and workflow software have low marginal cost and no reason to be usage priced. Hold vendors to that, and treat metered AI like cloud: a variable cost line with an owner, a monthly review, and a kill switch.",
  },
];

const BudgetImpact = () => (
  <section className="py-10 sm:py-12">
    <h2
      id="budget-impact"
      className="text-2xl sm:text-3xl font-bold text-foreground mb-4 flex items-center gap-2"
    >
      <DollarSign className="h-6 w-6 text-primary" />
      What it does to a budget, and what to do about it
    </h2>

    <div className="grid gap-4 sm:gap-6">
      {drivers.map((d) => (
        <div
          key={d.title}
          className="bg-card border border-border rounded-xl p-5 sm:p-6 hover:border-primary/30 transition-colors"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-primary/10 text-primary shrink-0">
              <d.icon className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {d.title}
              </h3>
              <p className="text-muted-foreground">{d.body}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const Sources = () => (
  <section className="py-10 sm:py-12">
    <h2
      id="sources"
      className="text-2xl sm:text-3xl font-bold text-foreground mb-4"
    >
      Sources and further reading
    </h2>
    <ul className="space-y-3 text-muted-foreground text-sm sm:text-base list-disc pl-5">
      <li>
        <Link
          to="/insights/microsoft-drops-claude-code"
          className="text-primary underline hover:no-underline"
        >
          Microsoft drops Claude Code after burning through its AI budget
        </Link>
      </li>
      <li>
        <Link
          to="/insights/ai-cost-vs-employees"
          className="text-primary underline hover:no-underline"
        >
          AI vs employees: the 2026 cost math
        </Link>
      </li>
      <li>
        <Link
          to="/insights/project-management-tools"
          className="text-primary underline hover:no-underline"
        >
          Project management tools: complete comparison guide
        </Link>
      </li>
      <li>
        <a
          href="https://www.salesforce.com/news/press-releases/2025/05/15/agentforce-flexible-pricing-news/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          Salesforce introduces Flex Credits pricing for Agentforce (press release, May 15, 2025)
        </a>
      </li>
      <li>
        <a
          href="https://help.salesforce.com/s/articleView?id=004811240&language=en_US&type=1"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          Salesforce Help: Agentforce pricing and Flex Credits
        </a>
      </li>
      <li>
        <a
          href="https://www.zendesk.com/blog/ai/agentic-ai/outcome-based-pricing/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          Zendesk: outcome based pricing for AI agents, per automated resolution
        </a>
      </li>
      <li>
        <a
          href="https://my.idc.com/getdoc.jsp?containerId=US53414425"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          IDC market note on Zendesk outcome based pricing
        </a>
      </li>
      <li>
        <a
          href="https://www.fintechweekly.com/magazine/articles/cursor-pricing-change-user-backlash-refund"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          Cursor pricing shift to usage based credits, June 2025, and the backlash (FinTech Weekly)
        </a>
      </li>
      <li>
        <a
          href="https://www.vantage.sh/blog/cursor-pricing-explained"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          Vantage: Cursor pricing explained
        </a>
      </li>
    </ul>
  </section>
);

const PerSeatPricingTokenEconomics = () => {
  return (
    <>
      <InsightSEO
        slug="per-seat-pricing-token-economics"
        additionalKeywords={[
          "per seat pricing",
          "usage based pricing SaaS",
          "token pricing",
          "AI credits pricing",
          "outcome based pricing",
          "SaaS budgeting AI",
          "Salesforce Flex Credits",
          "Cursor pricing change",
          "AI cost forecasting",
          "software procurement AI",
        ]}
      />
      <InsightEngagementTracker
        slug="per-seat-pricing-token-economics"
        title="Per Seat Is Dying: How Token Pricing Is Rewriting SaaS Budgets"
      />
      <InsightsNavbar showBack />
      <ScrollProgressIndicator />
      <TableOfContents />
      <InsightsLeadModal />
      <main className="page-container pb-16 sm:pb-20">
        <InsightsBreadcrumb
          category="AI Development"
          currentPage="Per Seat Pricing and Token Economics"
        />
        <HeroSection />

        <AnimatedSection delay={100} className="mt-10 sm:mt-12">
          <Takeaway />
        </AnimatedSection>

        <AnimatedSection delay={50} className="mt-8 sm:mt-10">
          <FounderQuote />
        </AnimatedSection>

        <div className="section-divider" />

        <AnimatedSection delay={0}>
          <MarketShift />
        </AnimatedSection>

        <AnimatedSection delay={0}>
          <BudgetImpact />
        </AnimatedSection>

        <AnimatedSection delay={0}>
          <Sources />
        </AnimatedSection>

        <AnimatedSection delay={0}>
          <InsightBenchmarks insightSlug="per-seat-pricing-token-economics" />
        </AnimatedSection>

        <AnimatedSection delay={0}>
          <InsightFAQSection insightSlug="per-seat-pricing-token-economics" />
        </AnimatedSection>

        <AnimatedSection delay={0}>
          <RelatedTools insightSlug="per-seat-pricing-token-economics" />
        </AnimatedSection>
      </main>
      <Footer />
    </>
  );
};

export default PerSeatPricingTokenEconomics;